import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSession } from '../../hooks/useSession';
import { 
  User, 
  Mail, 
  Lock, 
  Phone, 
  Eye, 
  EyeOff,
  Sparkles,
  Shield,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  UserPlus
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const { createSession } = useSession();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    // Calculate password strength
    const strength = calculatePasswordStrength(formData.password);
    setPasswordStrength(strength);
  }, [formData.password]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return Math.min(score, 4);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (formData.phone && !/^[0-9]{10,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Mock registration - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockUser = {
        id: 1,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name)}&background=8b5cf6&color=fff`,
      };
      
      createSession('mock-token-123', mockUser);
      toast.success('🎉 Account created successfully! Welcome aboard!');
      setTimeout(() => navigate('/'), 500);
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthLabel = () => {
    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    return labels[passwordStrength] || '';
  };

  const getStrengthColor = () => {
    const colors = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#22c55e'];
    return colors[passwordStrength] || '#ef4444';
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.06,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
  };

  return (
    <motion.div 
      className="auth-page register-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container-custom">
        <motion.div 
          className="auth-card register-card"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Decorative Elements */}
          <div className="auth-decoration">
            <motion.div 
              className="decoration-circle circle-1"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            <motion.div 
              className="decoration-circle circle-2"
              animate={{
                scale: [1, 1.3, 1],
                rotate: [360, 180, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>

          <motion.div 
            className="auth-header"
            variants={itemVariants}
          >
            <motion.div 
              className="auth-icon-wrapper"
              whileHover={{ scale: 1.1, rotate: 10 }}
              whileTap={{ scale: 0.9 }}
            >
              <UserPlus className="auth-icon" />
            </motion.div>
            <motion.h1 
              className="auth-title"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Create Account
            </motion.h1>
            <motion.p 
              className="auth-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Start trading bullion with confidence
            </motion.p>
          </motion.div>

          <motion.form 
            onSubmit={handleSubmit} 
            className="auth-form"
            variants={itemVariants}
          >
            <motion.div 
              className="form-group"
              variants={itemVariants}
            >
              <label className="form-label">
                <User size={16} className="label-icon" />
                Full Name
              </label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${touched.name && errors.name ? 'error' : ''} ${touched.name && !errors.name && formData.name ? 'valid' : ''}`}
                  placeholder="Enter your full name"
                  required
                />
                <AnimatePresence>
                  {touched.name && !errors.name && formData.name && (
                    <motion.span 
                      className="input-validation"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      <CheckCircle size={18} className="validation-success" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {touched.name && errors.name && (
                  <motion.span 
                    className="form-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertCircle size={14} />
                    {errors.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div 
              className="form-group"
              variants={itemVariants}
            >
              <label className="form-label">
                <Mail size={16} className="label-icon" />
                Email Address
              </label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${touched.email && errors.email ? 'error' : ''} ${touched.email && !errors.email && formData.email ? 'valid' : ''}`}
                  placeholder="Enter your email"
                  required
                />
                <AnimatePresence>
                  {touched.email && !errors.email && formData.email && (
                    <motion.span 
                      className="input-validation"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      <CheckCircle size={18} className="validation-success" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {touched.email && errors.email && (
                  <motion.span 
                    className="form-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertCircle size={14} />
                    {errors.email}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div 
              className="form-group"
              variants={itemVariants}
            >
              <label className="form-label">
                <Phone size={16} className="label-icon" />
                Phone Number
              </label>
              <div className="input-wrapper">
                <Phone size={18} className="input-icon" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                  placeholder="Enter your phone number"
                />
              </div>
              <AnimatePresence>
                {touched.phone && errors.phone && (
                  <motion.span 
                    className="form-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertCircle size={14} />
                    {errors.phone}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div 
              className="form-group"
              variants={itemVariants}
            >
              <label className="form-label">
                <Lock size={16} className="label-icon" />
                Password
              </label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${touched.password && errors.password ? 'error' : ''}`}
                  placeholder="Create a password"
                  required
                  minLength="6"
                />
                <motion.button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </motion.button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <motion.div 
                  className="password-strength"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="strength-bar">
                    {[...Array(4)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="strength-segment"
                        animate={{
                          background: i < passwordStrength ? getStrengthColor() : 'var(--border-color)',
                        }}
                        transition={{ duration: 0.3 }}
                      />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: getStrengthColor() }}>
                    {getStrengthLabel()}
                  </span>
                </motion.div>
              )}

              <AnimatePresence>
                {touched.password && errors.password && (
                  <motion.span 
                    className="form-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertCircle size={14} />
                    {errors.password}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div 
              className="form-group"
              variants={itemVariants}
            >
              <label className="form-label">
                <Lock size={16} className="label-icon" />
                Confirm Password
              </label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''} ${touched.confirmPassword && !errors.confirmPassword && formData.confirmPassword ? 'valid' : ''}`}
                  placeholder="Confirm your password"
                  required
                />
                <motion.button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </motion.button>
              </div>
              <AnimatePresence>
                {touched.confirmPassword && errors.confirmPassword && (
                  <motion.span 
                    className="form-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <AlertCircle size={14} />
                    {errors.confirmPassword}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.button
              type="submit"
              disabled={loading}
              className="auth-submit-btn"
              variants={itemVariants}
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
            >
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.span
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    <span className="spinner-btn" />
                    Creating Account...
                  </motion.span>
                ) : (
                  <motion.span
                    key="submit"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    Create Account
                    <Sparkles size={18} />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.form>

          <motion.div 
            className="auth-footer"
            variants={itemVariants}
          >
            <p>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Login
                <ArrowRight size={14} className="link-arrow" />
              </Link>
            </p>
          </motion.div>

          <motion.div 
            className="auth-security"
            variants={itemVariants}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Shield size={14} className="security-icon" />
            <span>Your data is protected with 256-bit encryption</span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Register;